import { useEffect, useMemo, useState } from "react";

import { useLiveMeetingStore } from "../../../meeting/live/store.ts";
import { useInterviewLiveStore } from "../store.ts";

/**
 * Client-side interview pacing. Runs entirely in the browser off a local ticking
 * clock so alerts have sub-second latency (the PRD requires < 1s) — no server
 * round-trip. Phases follow the standard structure: a short intro then the main
 * question phase, then wrap-up.
 */
export type InterviewPhase = "intro" | "questions" | "wrapup";

const INTRO_END_SEC = 6 * 60; // ~5-6 min introduction
const QUESTIONS_END_SEC = 36 * 60; // ~30 min question phase after intro

export interface PacingState {
  elapsedSec: number;
  phase: InterviewPhase;
  phaseLabel: string;
  /** Fraction 0..1 of the planned total (used for the progress bar). */
  progress: number;
  /** The most relevant pacing alert right now, or null. */
  alert: { level: "info" | "warn"; text: string } | null;
}

function phaseFor(elapsed: number): InterviewPhase {
  if (elapsed < INTRO_END_SEC) return "intro";
  if (elapsed < QUESTIONS_END_SEC) return "questions";
  return "wrapup";
}

export function useInterviewPacing(): PacingState {
  // Shared live state (transcript/listening/startedAt) comes from the reused
  // meeting store; the question checklist is interview-only.
  const startedAt = useLiveMeetingStore((s) => s.startedAt);
  const listening = useLiveMeetingStore((s) => s.listening);
  const transcript = useLiveMeetingStore((s) => s.transcript);
  const checklist = useInterviewLiveStore((s) => s.checklist);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!listening) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [listening]);

  return useMemo(() => {
    const elapsedSec = startedAt && listening ? Math.max(0, Math.floor((now - startedAt) / 1000)) : 0;
    const phase = phaseFor(elapsedSec);
    const unasked = checklist.filter((c) => !c.asked).length;

    // Estimate how long the candidate ("client") has been speaking in the most
    // recent uninterrupted streak — a long streak → suggest wrapping/interrupting.
    let candidateStreakSec = 0;
    for (let i = transcript.length - 1; i >= 0; i--) {
      const line = transcript[i]!;
      if (line.speaker !== "client") break;
      // Rough: ~0.4s per word of spoken content.
      candidateStreakSec += Math.max(3, line.text.split(/\s+/).length * 0.4);
    }

    let alert: PacingState["alert"] = null;
    if (phase === "intro") {
      const left = INTRO_END_SEC - elapsedSec;
      if (left <= 60 && left > 0) {
        alert = { level: "warn", text: `~1 minute left for the introduction — move into questions soon.` };
      } else if (left <= 0) {
        alert = { level: "warn", text: `You've spent the full intro time. Start the questions.` };
      }
    } else if (phase === "questions") {
      const left = QUESTIONS_END_SEC - elapsedSec;
      if (candidateStreakSec > 210) {
        alert = {
          level: "warn",
          text: `The candidate has been answering for a while — consider politely wrapping up this answer to stay on time.`,
        };
      } else if (left <= 5 * 60 && unasked > 0) {
        alert = {
          level: "warn",
          text: `~${Math.ceil(left / 60)} min left and ${unasked} question(s) unasked — pick up the pace.`,
        };
      }
    } else {
      alert = { level: "warn", text: `You're over the planned time — begin wrapping up.` };
    }

    const label =
      phase === "intro" ? "Introduction" : phase === "questions" ? "Questions" : "Wrap-up";
    const progress = Math.min(1, elapsedSec / QUESTIONS_END_SEC);

    return { elapsedSec, phase, phaseLabel: label, progress, alert };
  }, [startedAt, listening, now, transcript, checklist]);
}
